import { useMemo } from 'react'
import { Checkbox } from '@/components/ui/checkbox'
import { cn } from '@/lib/utils'
import type { PermissionCatalogEntry } from '@/api/usersApi'

type Props = {
  catalog: PermissionCatalogEntry[]
  value: string[]
  onChange: (keys: string[]) => void
  disabled?: boolean
}

/**
 * Controlled grouped checkbox matrix for picking permission keys. Groups come
 * from each catalog entry's `group`. Each group has a select-all toggle.
 */
export function RolePermissionMatrix({ catalog, value, onChange, disabled }: Props) {
  const groups = useMemo(() => {
    const byGroup = new Map<string, PermissionCatalogEntry[]>()
    for (const entry of catalog) {
      const list = byGroup.get(entry.group)
      if (list) list.push(entry)
      else byGroup.set(entry.group, [entry])
    }
    return [...byGroup.entries()]
  }, [catalog])

  const selected = useMemo(() => new Set(value), [value])

  const setKeys = (next: Set<string>) => onChange([...next])

  const toggle = (key: string) => {
    const next = new Set(selected)
    if (next.has(key)) next.delete(key)
    else next.add(key)
    setKeys(next)
  }

  const toggleGroup = (entries: PermissionCatalogEntry[]) => {
    const keys = entries.map((e) => e.key)
    const allOn = keys.every((k) => selected.has(k))
    const next = new Set(selected)
    for (const k of keys) {
      if (allOn) next.delete(k)
      else next.add(k)
    }
    setKeys(next)
  }

  return (
    <div className='space-y-5'>
      {groups.map(([group, entries]) => {
        const allOn = entries.every((e) => selected.has(e.key))
        const someOn = entries.some((e) => selected.has(e.key))
        return (
          <div key={group} className='rounded-lg border'>
            <button
              type='button'
              disabled={disabled}
              onClick={() => toggleGroup(entries)}
              className='flex w-full items-center justify-between gap-2 border-b bg-muted/40 px-3 py-2 text-left'
            >
              <span className='text-sm font-semibold'>{group}</span>
              <span
                className={cn(
                  'text-xs',
                  allOn ? 'text-blue-600' : someOn ? 'text-amber-600' : 'text-muted-foreground',
                )}
              >
                {entries.filter((e) => selected.has(e.key)).length}/{entries.length}
              </span>
            </button>
            <div className='divide-y'>
              {entries.map((entry) => (
                <label
                  key={entry.key}
                  className='flex cursor-pointer items-start gap-3 px-3 py-2.5 hover:bg-muted/30'
                >
                  <Checkbox
                    className='mt-0.5'
                    checked={selected.has(entry.key)}
                    disabled={disabled}
                    onCheckedChange={() => toggle(entry.key)}
                  />
                  <span className='min-w-0'>
                    <span className='block text-sm font-medium'>{entry.label}</span>
                    <span className='block text-xs text-muted-foreground'>
                      {entry.description}
                    </span>
                    <code className='mt-0.5 inline-block rounded bg-muted px-1 text-[10px] text-muted-foreground'>
                      {entry.key}
                    </code>
                  </span>
                </label>
              ))}
            </div>
          </div>
        )
      })}
    </div>
  )
}
